// Generieke schijven-engine voor prijslijstposten met tarieven per schijf.
// Pure functies, gedeeld tussen frontend en backend. Referentie-implementatie:
// FILES CALCULATIE/gevel_schijven.py (SPEC_calculatie_gevel_crepi.md hoofdstuk 6).
import { z } from 'zod';

/**
 * Schijven van één post.
 *  - progressief: elke schijf rekent enkel het deel dat erin valt (zoals belastingschijven)
 *  - per_klasse:  de volledige hoeveelheid aan het tarief van de klasse waarin het totaal valt
 * `grenzen` zijn de bovengrenzen van alle schijven behalve de laatste (open) schijf, dus
 * `tarieven.length === grenzen.length + 1`.
 */
export const schijvenConfigSchema = z.object({
  type: z.enum(['progressief', 'per_klasse']),
  grenzen: z.array(z.number().positive()),
  tarieven: z.array(z.number().nonnegative()),
});
export type SchijvenConfig = z.infer<typeof schijvenConfigSchema>;

export const schijfRegelSchema = z.object({
  /** Mens-leesbaar label, bv. "0–50 m2" of "200+ m2". */
  schijf: z.string(),
  hoeveelheid: z.number(),
  prijs: z.number(),
  bedrag: z.number(),
});
export type SchijfRegel = z.infer<typeof schijfRegelSchema>;

/** Afronding op cent, half-up (zoals Decimal ROUND_HALF_UP in de referentie). */
export function round2(x: number): number {
  return Math.round((x + Math.sign(x) * Number.EPSILON) * 100) / 100;
}

/** Hoeveelheden afronden op 4 decimalen om float-ruis (0.1 + 0.2) uit de ladder te houden. */
function roundQty(x: number): number {
  return Math.round(x * 10000) / 10000;
}

function fmtGrens(n: number): string {
  return String(roundQty(n)).replace('.', ',');
}

export function schijfLabel(van: number, tot: number | null, eenheid = ''): string {
  const suffix = eenheid ? ' ' + eenheid.toLowerCase() : '';
  return tot == null ? `${fmtGrens(van)}+${suffix}` : `${fmtGrens(van)}–${fmtGrens(tot)}${suffix}`;
}

export function validateSchijven(cfg: SchijvenConfig): void {
  if (cfg.tarieven.length !== cfg.grenzen.length + 1) {
    throw new Error(`Schijven: ${cfg.grenzen.length} grenzen vereisen ${cfg.grenzen.length + 1} tarieven`);
  }
  for (let i = 1; i < cfg.grenzen.length; i++) {
    if (cfg.grenzen[i]! <= cfg.grenzen[i - 1]!) throw new Error('Schijven: grenzen moeten strikt stijgen');
  }
}

export interface SchijvenResultaat {
  regels: SchijfRegel[];
  totaal: number;
  /** Positie op de ladder na deze hoeveelheid — voor ladders die over meerdere delen doorlopen. */
  eindPositie: number;
}

/**
 * Rekent een hoeveelheid uit over de schijven.
 * @param startPositie waar de ladder al staat (progressief): het tweede deel van de crepi
 *   gaat verder waar het eerste deel stopte en begint niet opnieuw bij schijf 1.
 */
export function berekenSchijven(
  hoeveelheid: number,
  cfg: SchijvenConfig,
  startPositie = 0,
  eenheid = '',
): SchijvenResultaat {
  validateSchijven(cfg);
  if (hoeveelheid < 0) throw new Error('Hoeveelheid kan niet negatief zijn');
  const regels: SchijfRegel[] = [];
  let positie = roundQty(startPositie);
  let rest = roundQty(hoeveelheid);

  if (cfg.type === 'per_klasse') {
    if (rest > 0) {
      const eind = roundQty(positie + rest);
      // Klasse van het totaal: de eerste schijf waarvan de bovengrens >= totaal.
      let i = cfg.grenzen.findIndex((g) => eind <= g);
      if (i === -1) i = cfg.grenzen.length;
      const van = i === 0 ? 0 : cfg.grenzen[i - 1]!;
      const tot = i < cfg.grenzen.length ? cfg.grenzen[i]! : null;
      const prijs = cfg.tarieven[i]!;
      regels.push({ schijf: schijfLabel(van, tot, eenheid), hoeveelheid: rest, prijs, bedrag: round2(rest * prijs) });
      positie = eind;
    }
  } else {
    while (rest > 0) {
      let i = cfg.grenzen.findIndex((g) => positie < g);
      if (i === -1) i = cfg.grenzen.length;
      const van = i === 0 ? 0 : cfg.grenzen[i - 1]!;
      const tot = i < cfg.grenzen.length ? cfg.grenzen[i]! : null;
      const ruimte = tot == null ? rest : roundQty(Math.min(rest, tot - positie));
      const prijs = cfg.tarieven[i]!;
      regels.push({ schijf: schijfLabel(van, tot, eenheid), hoeveelheid: ruimte, prijs, bedrag: round2(ruimte * prijs) });
      positie = roundQty(positie + ruimte);
      rest = roundQty(rest - ruimte);
    }
  }

  return { regels, totaal: round2(regels.reduce((s, r) => s + r.bedrag, 0)), eindPositie: positie };
}

// === Crepi: één ladder per project over twee delen ===

export const crepiLadderSchema = z.object({
  grenzen: z.array(z.number().positive()),
  metIsolatie: z.array(z.number().nonnegative()),
  zonderIsolatie: z.array(z.number().nonnegative()),
});
export type CrepiLadder = z.infer<typeof crepiLadderSchema>;

export type CrepiDeel = 'met_isolatie' | 'zonder_isolatie';

export const CREPI_DEEL_LABEL: Record<CrepiDeel, string> = {
  met_isolatie: 'Crepi met isolatie',
  zonder_isolatie: 'Crepi zonder isolatie',
};

export interface CrepiRegel extends SchijfRegel {
  deel: CrepiDeel;
}

export interface CrepiResultaat {
  m2Crepi: number;
  m2Isolatie: number;
  m2Met: number;
  m2Zonder: number;
  /** Welk deel de ladder eerst vult. */
  eerst: CrepiDeel;
  regels: CrepiRegel[];
  totaalMet: number;
  totaalZonder: number;
  totaal: number;
  waarschuwingen: string[];
}

/**
 * Progressieve crepi-ladder (SPEC_calculatie 6, SPEC_wijzigingen 1.1):
 *  - één ladder op de totale crepi-oppervlakte;
 *  - het grootste deel (met of zonder isolatie) vult de ladder eerst, bij exact 50/50 met isolatie;
 *  - het tweede deel gaat verder waar de ladder staat.
 */
export function berekenCrepi(m2Crepi: number, m2Isolatie: number, ladder: CrepiLadder): CrepiResultaat {
  if (m2Crepi < 0 || m2Isolatie < 0) throw new Error('Oppervlakte kan niet negatief zijn');
  const crepi = roundQty(m2Crepi);
  const isolatie = roundQty(m2Isolatie);
  const waarschuwingen: string[] = [];

  const met = Math.min(isolatie, crepi);
  const zonder = roundQty(crepi - met);
  if (isolatie > crepi) {
    waarschuwingen.push(
      `${fmtGrens(roundQty(isolatie - crepi))} m2 isolatie zonder crepi-afwerking: geen post in de prijslijst, manueel beslissen.`,
    );
  }

  const eerst: CrepiDeel = met >= zonder ? 'met_isolatie' : 'zonder_isolatie';
  const volgorde: CrepiDeel[] = eerst === 'met_isolatie' ? ['met_isolatie', 'zonder_isolatie'] : ['zonder_isolatie', 'met_isolatie'];
  if (met === zonder && met > 0) {
    waarschuwingen.push('Verdeling exact 50/50: crepi met isolatie eerst genomen (spec 12.2).');
  }

  const hoeveelheden: Record<CrepiDeel, number> = { met_isolatie: met, zonder_isolatie: zonder };
  const tarieven: Record<CrepiDeel, number[]> = { met_isolatie: ladder.metIsolatie, zonder_isolatie: ladder.zonderIsolatie };
  const regels: CrepiRegel[] = [];
  let positie = 0;
  for (const deel of volgorde) {
    const r = berekenSchijven(hoeveelheden[deel], { type: 'progressief', grenzen: ladder.grenzen, tarieven: tarieven[deel] }, positie, 'm2');
    for (const regel of r.regels) regels.push({ ...regel, deel });
    positie = r.eindPositie;
  }

  const totaalMet = round2(regels.filter((r) => r.deel === 'met_isolatie').reduce((s, r) => s + r.bedrag, 0));
  const totaalZonder = round2(regels.filter((r) => r.deel === 'zonder_isolatie').reduce((s, r) => s + r.bedrag, 0));
  return {
    m2Crepi: crepi,
    m2Isolatie: isolatie,
    m2Met: met,
    m2Zonder: zonder,
    eerst,
    regels,
    totaalMet,
    totaalZonder,
    totaal: round2(totaalMet + totaalZonder),
    waarschuwingen,
  };
}
